/**
 * Entitlements Store
 *
 * Pinia store for managing entitlements state with caching.
 * Both console and frontend apps can use this store.
 *
 * Features:
 * - Caches entitlements data per client
 * - Auto-invalidates cache on mutations
 * - Loading states for async operations
 * - Error handling
 * - Uses dependency injection for EntitlementsService
 */

import { defineStore } from 'pinia'
import { ref, computed, inject } from 'vue'
import type { EntitlementsService } from './service'
import type { ClientEntitlements } from './types'
import { Module } from './enums'
import type { ModuleValue, TierValue } from './enums'

/**
 * Injection key for EntitlementsService
 * This must be defined and exported by the consuming app (console or frontend)
 */
export const ENTITLEMENTS_SERVICE_KEY = Symbol('entitlementsService') as symbol

export const useEntitlementsStore = defineStore('entitlements', () => {
  // =============================================================================
  // DEPENDENCY INJECTION
  // =============================================================================

  const service = inject<EntitlementsService>(ENTITLEMENTS_SERVICE_KEY)!
  if (!service) {
    throw new Error(
      'EntitlementsService not provided. Ensure app.provide(ENTITLEMENTS_SERVICE_KEY, service) is called in main.ts'
    )
  }

  // =============================================================================
  // STATE
  // =============================================================================

  const cache = ref(new Map<number, ClientEntitlements>())
  const loading = ref(new Map<number, boolean>())
  const errors = ref(new Map<number, string | null>())

  // =============================================================================
  // GETTERS
  // =============================================================================

  /**
   * Get cached entitlements for a client
   */
  const getEntitlements = computed(() => {
    return (clientId: number): ClientEntitlements | null => {
      return cache.value.get(clientId) || null
    }
  })

  /**
   * Get modules for a client (from cache)
   */
  const getModules = computed(() => {
    return (clientId: number): Module[] | null => {
      const entitlements = cache.value.get(clientId)
      return entitlements?.modules || null
    }
  })

  /**
   * Check if a client has a specific module (from cache)
   */
  const hasModule = computed(() => {
    return (clientId: number, module: Module): boolean => {
      const modules = getModules.value(clientId)
      return modules ? modules.includes(module) : false
    }
  })

  /**
   * Check if entitlements are currently loading for a client
   */
  const isLoading = computed(() => {
    return (clientId: number): boolean => {
      return loading.value.get(clientId) || false
    }
  })

  /**
   * Get error for a client
   */
  const getError = computed(() => {
    return (clientId: number): string | null => {
      return errors.value.get(clientId) || null
    }
  })

  // =============================================================================
  // ACTIONS
  // =============================================================================

  /**
   * Fetch and cache entitlements for a client
   *
   * @param clientId - Client ID to fetch entitlements for
   * @param forceRefresh - Skip cache and fetch fresh data
   * @returns Client entitlements
   */
  async function fetchEntitlements(
    clientId: number,
    forceRefresh = false
  ): Promise<ClientEntitlements> {
    // Return cached data if available and not forcing refresh
    if (!forceRefresh && cache.value.has(clientId)) {
      return cache.value.get(clientId)!
    }

    // Set loading state
    loading.value.set(clientId, true)
    errors.value.set(clientId, null)

    try {
      const entitlements = await service.getClientEntitlements(clientId)

      // Update cache
      cache.value.set(clientId, entitlements)

      return entitlements
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch entitlements'
      errors.value.set(clientId, errorMessage)
      throw error
    } finally {
      loading.value.set(clientId, false)
    }
  }

  /**
   * Check if a client has a module (fetches if not cached)
   *
   * @param clientId - Client ID to check
   * @param module - Module key to check
   * @returns True if client has access to the module
   */
  async function checkModule(clientId: number, module: Module): Promise<boolean> {
    // Check cache first
    if (cache.value.has(clientId)) {
      return hasModule.value(clientId, module)
    }

    // Fetch if not cached
    const entitlements = await fetchEntitlements(clientId)
    return entitlements.modules.includes(module)
  }

  /**
   * Grant a module to a client (admin only)
   *
   * Invalidates cache and refetches.
   *
   * @param clientId - Client ID
   * @param moduleKey - Module key to grant
   * @param reason - Optional reason
   * @returns Updated entitlements
   */
  async function grantModule(
    clientId: number,
    moduleKey: ModuleValue,
    reason?: string
  ): Promise<ClientEntitlements> {
    try {
      const entitlements = await service.grantModule(clientId, moduleKey, reason)

      // Update cache with fresh data
      cache.value.set(clientId, entitlements)

      return entitlements
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to grant module'
      errors.value.set(clientId, errorMessage)
      throw error
    }
  }

  /**
   * Deny a module for a client (admin only)
   *
   * Invalidates cache and refetches.
   */
  async function denyModule(
    clientId: number,
    moduleKey: ModuleValue,
    reason?: string
  ): Promise<ClientEntitlements> {
    try {
      const entitlements = await service.denyModule(clientId, moduleKey, reason)

      // Update cache with fresh data
      cache.value.set(clientId, entitlements)

      return entitlements
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to deny module'
      errors.value.set(clientId, errorMessage)
      throw error
    }
  }

  /**
   * Revoke a module exception (admin only)
   *
   * Invalidates cache and refetches.
   */
  async function revokeModuleException(
    clientId: number,
    moduleKey: ModuleValue
  ): Promise<ClientEntitlements> {
    try {
      const entitlements = await service.revokeModuleException(clientId, moduleKey)

      // Update cache with fresh data
      cache.value.set(clientId, entitlements)

      return entitlements
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to revoke module'
      errors.value.set(clientId, errorMessage)
      throw error
    }
  }

  /**
   * Change a client's tier (admin only)
   *
   * Invalidates cache and refetches.
   */
  async function changeClientTier(clientId: number, tier: TierValue): Promise<ClientEntitlements> {
    try {
      const entitlements = await service.changeClientTier(clientId, tier)

      // Update cache with fresh data
      cache.value.set(clientId, entitlements)

      return entitlements
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to change tier'
      errors.value.set(clientId, errorMessage)
      throw error
    }
  }

  /**
   * Clear cached entitlements for a client
   */
  function clearCache(clientId: number) {
    cache.value.delete(clientId)
    loading.value.delete(clientId)
    errors.value.delete(clientId)
  }

  /**
   * Clear all cached entitlements
   */
  function clearAllCache() {
    cache.value.clear()
    loading.value.clear()
    errors.value.clear()
  }

  return {
    // State (for debugging)
    cache,
    loading,
    errors,

    // Service (for advanced usage)
    service,

    // Getters
    getEntitlements,
    getModules,
    hasModule,
    isLoading,
    getError,

    // Actions
    fetchEntitlements,
    checkModule,
    grantModule,
    denyModule,
    revokeModuleException,
    changeClientTier,
    clearCache,
    clearAllCache
  }
})
